// Cisco IOS command parser.
//
// Commands are declared as space-separated patterns, e.g.
//   'show ip interface brief'
//   'ip address <A.B.C.D> <A.B.C.D>'
//   'interface <IF>'
// and compiled into a per-mode trie. Matching follows IOS rules:
//   * keywords may be abbreviated to any unique prefix ("sh ip int br")
//   * an ambiguous prefix  → "% Ambiguous command"
//   * an unknown token     → "% Invalid input detected at '^' marker."
//   * a missing argument   → "% Incomplete command."
// Parameters are validated by type so that "ip address 300.1.1.1 ..." fails at
// the right caret position, like the real parser.

import { isIp } from '../net/addr.js';
import { parseIfName, parseIfNameCreate } from './ifnames.js';

export const PARAMS = {
  '<A.B.C.D>': { help: 'A.B.C.D', test: (t) => isIp(t) },
  '<WORD>': { help: 'WORD', test: (t) => t.length > 0 },
  '<LINE>': { help: 'LINE', rest: true },
  '<IFRANGE>': { help: 'Interface range', rest: true },
  '<HOST>': { help: 'WORD  Ping destination address or hostname', test: (t) => t.length > 0 },
  '<IPV6PFX>': { help: 'X:X:X:X::X/<0-128>', test: (t) => /^[0-9a-f:]+\/\d{1,3}$/i.test(t) },
  '<IF>': { help: 'Interface', iface: true },
  '<IFC>': { help: 'Interface', iface: true, create: true },
  '<VLANLIST>': { help: 'WORD  VLAN IDs of the allowed VLANs', test: (t) => /^\d+(-\d+)?(,\d+(-\d+)?)*$/.test(t) },
  '<FLASHFILE>': { help: 'flash:  File to use (flash:<filename>)', test: (t) => /^flash0?:\/?[\w.\-]+$/i.test(t) },
  '<PORT>': { help: '<0-65535>  Port number', test: (t) => (/^\d+$/.test(t) && +t <= 65535) || /^(ftp-data|ftp|telnet|smtp|tacacs|domain|www|pop3|bgp|bootps|bootpc|tftp|ntp|snmp|snmptrap|isakmp|syslog)$/i.test(t) },
};

// Descriptions shown by "?" help.
export const KEYWORD_HELP = {
  enable: 'Turn on privileged commands', disable: 'Turn off privileged commands',
  exit: 'Exit from the EXEC', end: 'Exit from configure mode', logout: 'Exit from the EXEC',
  show: 'Show running system information', ping: 'Send echo messages', traceroute: 'Trace route to destination',
  configure: 'Enter configuration mode', terminal: 'Configure from the terminal', copy: 'Copy from one file to another',
  write: 'Write running configuration to memory', memory: 'Write to NV memory', clear: 'Reset functions',
  interface: 'Select an interface to configure', interfaces: 'Interface status and configuration',
  hostname: 'Set system\'s network name', ip: 'Global IP configuration subcommands', ipv6: 'IPv6 configuration',
  no: 'Negate a command or set its defaults', description: 'Interface specific description',
  shutdown: 'Shutdown the selected interface', address: 'Set the IP address of an interface',
  switchport: 'Set switching mode characteristics', mode: 'Set trunking mode of the interface',
  access: 'Set access mode characteristics of the interface', trunk: 'Set trunking characteristics of the interface',
  vlan: 'Vlan commands', brief: 'Brief summary', 'running-config': 'Current operating configuration',
  'startup-config': 'Contents of startup configuration', version: 'System hardware and software status',
  route: 'IP routing table', arp: 'ARP table', 'mac': 'MAC configuration', 'address-table': 'MAC forwarding table',
  status: 'Show interface line status', cdp: 'CDP information', neighbors: 'CDP neighbor entries',
  nat: 'NAT configuration commands', translations: 'Translation entries', inside: 'Inside interface for address translation',
  outside: 'Outside interface for address translation', speed: 'Configure speed operation.',
  duplex: 'Configure duplex operation.', do: 'To run exec commands in config mode', range: 'interface range command',
  name: 'Ascii name of the VLAN', 'domain-lookup': 'Enable IP Domain Name System hostname translation',
  'unicast-routing': 'Enable unicast routing', dhcp: 'Configure DHCP server and relay parameters',
  pool: 'Configure DHCP address pools', network: 'Network number and mask', 'default-router': 'Default routers',
  'dns-server': 'DNS servers', 'excluded-address': 'Prevent DHCP from assigning certain addresses',
  binding: 'DHCP address bindings', 'access-list': 'Add an access list entry', permit: 'Specify packets to forward',
  deny: 'Specify packets to reject', any: 'Any source host', host: 'A single host address', history: 'Display the session command history',
  dynamic: 'dynamic entry type', source: 'Source address translation', list: 'Specify access list describing local addresses',
  overload: 'Overload an address translation', 'startup-config ': '', clock: 'Display the system clock',
  'link-local': 'Use link-local address', helper: '', 'helper-address': 'Specify a destination address for UDP broadcasts',
  statistics: 'Translation statistics', vtp: 'Configure VLAN database', auto: 'Enable AUTO', full: 'Force full duplex operation',
  half: 'Force half-duplex operation', '10': 'Force 10 Mbps operation', '100': 'Force 100 Mbps operation', '1000': 'Force 1000 Mbps operation',
  counters: 'Clear counters on one or all interfaces',
  secret: 'Assign the privileged level secret', password: 'Assign the password', service: 'Modify use of network based services',
  'password-encryption': 'Encrypt system passwords', username: 'Establish User Name Authentication', privilege: 'Set user privilege level',
  'domain-name': 'Define the default domain name', crypto: 'Encryption module', key: 'Long term key operations', generate: 'Generate new keys',
  rsa: 'Generate RSA keys', modulus: 'Provide number of modulus bits on the command line', ssh: 'Configure ssh options',
  line: 'Configure a terminal line', console: 'Primary terminal line', vty: 'Virtual terminal', login: 'Enable password checking',
  local: 'Local password checking', transport: 'Define transport protocols for line', input: 'Define which protocols to use when connecting to the terminal server',
  'access-class': 'Filter connections based on an IP access list', 'exec-timeout': 'Set the EXEC timeout', banner: 'Define a login banner',
  motd: 'Set Message of the Day banner', aaa: 'Authentication, Authorization and Accounting.', 'new-model': 'Enable NEW access control commands and functions.',
  authentication: 'Authentication configurations parameters.', accounting: 'Accounting configurations parameters.', radius: 'Use RADIUS', tacacs: 'Modify TACACS query parameters',
  group: 'Use Server-group', 'access-group': 'Specify access control for packets', 'access-lists': 'List access lists', extended: 'Extended Access List',
  standard: 'Standard Access List', established: 'Match established connections', log: 'Log matches against this entry', remark: 'Access list entry comment',
  'port-security': 'Security related command', maximum: 'Max secure addresses', violation: 'Security violation mode', sticky: 'Configure dynamic secure addresses as sticky',
  protect: 'Security violation protect mode', restrict: 'Security violation restrict mode', 'mac-address': 'Secure mac address',
};

/** Tokenise, keeping track of each token's column for caret placement. */
export function tokenize(line) {
  const out = [];
  const re = /\S+/g;
  let m;
  while ((m = re.exec(line))) out.push({ t: m[0], col: m.index });
  return out;
}

export function compile(specs) {
  const root = { kw: {}, params: [], cmd: null };
  for (const s of specs) {
    let node = root;
    for (const part of s.pattern.split(' ')) {
      if (part.startsWith('<')) {
        let p = node.params.find((x) => x.type === part);
        if (!p) {
          const range = part.match(/^<(\d+)-(\d+)>$/);
          p = { type: part, node: { kw: {}, params: [], cmd: null }, range: range ? [+range[1], +range[2]] : null };
          node.params.push(p);
        }
        node = p.node;
      } else {
        node.kw[part] = node.kw[part] || { kw: {}, params: [], cmd: null };
        node = node.kw[part];
      }
    }
    node.cmd = s;
  }
  return root;
}

function paramAccepts(p, tokens, i, ctx) {
  // Returns number of tokens consumed (0 = no match).
  if (p.range) {
    const n = Number(tokens[i].t);
    return /^\d+$/.test(tokens[i].t) && n >= p.range[0] && n <= p.range[1] ? 1 : 0;
  }
  const def = PARAMS[p.type];
  if (!def) return 0;
  if (def.rest) return tokens.length - i;
  if (def.iface) {
    // "fa0/1" or "fa 0/1"
    const parse = def.create ? parseIfNameCreate : parseIfName;
    const one = parse(tokens[i].t, ctx.device);
    if (one) return 1;
    if (i + 1 < tokens.length && parse(tokens[i].t + tokens[i + 1].t, ctx.device)) return 2;
    return 0;
  }
  return def.test(tokens[i].t) ? 1 : 0;
}

/**
 * Match a line against a compiled trie.
 * Result: { ok, cmd, args[], words[] } or { error: 'invalid'|'ambiguous'|'incomplete', col, token }
 */
export function match(root, line, ctx) {
  const tokens = tokenize(line);
  let node = root;
  const args = [];
  const words = [];
  let i = 0;
  while (i < tokens.length) {
    const tok = tokens[i];
    const low = tok.t.toLowerCase();
    const kws = Object.keys(node.kw);
    const exact = kws.find((k) => k === low);
    const pref = exact ? [exact] : kws.filter((k) => k.startsWith(low));
    if (pref.length === 1) {
      words.push(pref[0]);
      node = node.kw[pref[0]];
      i++;
      continue;
    }
    // Parameters get a chance before declaring ambiguity (e.g. numeric args).
    let took = 0, pm = null;
    for (const p of node.params) {
      took = paramAccepts(p, tokens, i, ctx);
      if (took) { pm = p; break; }
    }
    if (pm) {
      const raw = tokens.slice(i, i + took).map((x) => x.t);
      args.push(PARAMS[pm.type]?.rest ? line.slice(tokens[i].col).trim() : raw.join(''));
      words.push(pm.type);
      node = pm.node;
      i += took;
      continue;
    }
    if (pref.length > 1) return { error: 'ambiguous', col: tok.col, token: tok.t, options: pref };
    return { error: 'invalid', col: tok.col, token: tok.t, depth: i };
  }
  if (!node.cmd) return { error: 'incomplete', node };
  return { ok: true, cmd: node.cmd, args, words, node };
}

/** "?" help: list completions for the partial line. */
export function help(root, line, ctx) {
  const endsWithSpace = /\s$/.test(line) || line === '';
  const body = endsWithSpace ? line : line.replace(/\S+$/, '');
  const partial = endsWithSpace ? '' : line.match(/\S+$/)[0].toLowerCase();
  let node = root;
  if (body.trim()) {
    const r = match(root, body, ctx);
    if (r.error === 'incomplete') node = r.node;
    else if (r.ok) node = r.node;
    else return { error: r.error, col: r.col };
  }
  if (!endsWithSpace) {
    const words = Object.keys(node.kw).filter((k) => k.startsWith(partial)).sort();
    return { words };
  }
  const rows = Object.keys(node.kw).sort().map((k) => [k, KEYWORD_HELP[k] || '']);
  for (const p of node.params) rows.push([p.range ? `<${p.range[0]}-${p.range[1]}>` : PARAMS[p.type]?.help || p.type, '']);
  if (node.cmd) rows.push(['<cr>', '']);
  return { rows };
}

/** Tab completion of the last keyword. */
export function complete(root, line, ctx) {
  if (/\s$/.test(line) || !line) return null;
  const h = help(root, line, ctx);
  if (h.words?.length === 1) return line.replace(/\S+$/, h.words[0]) + ' ';
  return null;
}
